import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CompassLayout from "@/components/compass/CompassLayout";
import { useCompass } from "@/context/CompassContext";
import { ArrowRight, Storefront } from "@phosphor-icons/react";

// Path B: Ya Tengo un Negocio — Business Profile + Assessment
const INDUSTRIES = [
  "Retail",
  "Alimentos y Bebidas",
  "Belleza y Bienestar",
  "Fitness",
  "Servicios Profesionales",
  "Oficios / Construcción",
  "Distribución / Importación",
  "E-commerce",
  "Creativo / Medios",
  "Turismo",
  "Otro",
];

const CURRENT_STAGE = [
  "Idea validada",
  "Recién lanzado",
  "Primer año",
  "En crecimiento",
  "Estable",
  "Buscando expandir",
];

const YEARS = ["Menos de 1 año", "1-3 años", "3-5 años", "Más de 5 años"];

const TEAM_SIZE = ["Solo yo", "2-5 personas", "6-15 personas", "Más de 15 personas"];

const CHALLENGES = [
  "Ventas",
  "Marketing",
  "Flujo de Caja",
  "Inventario",
  "Operaciones",
  "Personal",
  "Expansión",
  "Procesos",
  "Proveedores",
];

export default function CompassNegocio() {
  const nav = useNavigate();
  const { draft, setAnswer } = useCompass();

  useEffect(() => {
    if (!draft.city) { nav("/brujula/ciudad", { replace: true }); return; }
    if (draft.path !== "negocio") { nav("/brujula", { replace: true }); }
  }, [draft, nav]);

  const requiredKeys = ["business_name", "industria", "etapa_actual", "anios", "desafio"];
  const answered = requiredKeys.every((k) => !!(draft.answers[k] && String(draft.answers[k]).trim()));

  const submit = () => nav("/brujula/analizando");

  const Group = ({ label, options, keyName, testidPrefix, required = true }) => (
    <div>
      <div className="label-mono mb-2 text-muted-foreground">
        {label}{!required && <span className="ml-2 normal-case opacity-70">(opcional)</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => {
          const selected = draft.answers[keyName] === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => setAnswer(keyName, o)}
              data-testid={`${testidPrefix}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-left border-2 border-foreground p-3 text-sm transition-colors ${
                selected ? "bg-foreground text-background" : "bg-surface hover:bg-muted"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <CompassLayout back="/brujula/ciudad" step={3} total={4} testid="page-compass-negocio">
      <div className="mb-6">
        <div className="label-mono text-muted-foreground">Paso 3 de 4 · Ya Tengo un Negocio</div>
        <h1 className="display text-3xl md:text-4xl mt-2 leading-[1]">Cuéntame de tu negocio</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Con este perfil, la Brújula podrá recomendarte enfoques específicos para tu situación.
        </p>
      </div>

      {/* ---------- Business Profile ---------- */}
      <section className="brut-card mb-6 space-y-5" data-testid="section-perfil-negocio">
        <div className="flex items-center gap-2">
          <Storefront size={18} weight="bold" />
          <div className="label-mono">Perfil del negocio</div>
        </div>

        <label className="block">
          <div className="label-mono mb-2 text-muted-foreground">Nombre del negocio</div>
          <input
            className="input-brut"
            value={draft.answers.business_name || ""}
            onChange={(e) => setAnswer("business_name", e.target.value)}
            placeholder="Ej. Alma Textil"
            data-testid="input-business-name"
          />
        </label>

        <Group label="¿Qué tipo de negocio tienes?" options={INDUSTRIES} keyName="industria" testidPrefix="ind" />
        <Group label="¿En qué etapa se encuentra hoy?" options={CURRENT_STAGE} keyName="etapa_actual" testidPrefix="stage" />
        <Group label="¿Cuántos años tiene tu negocio?" options={YEARS} keyName="anios" testidPrefix="anos" />
        <Group label="¿Cuántas personas trabajan contigo?" options={TEAM_SIZE} keyName="equipo" testidPrefix="team" required={false} />
      </section>

      {/* ---------- Assessment ---------- */}
      <section className="brut-card space-y-5" data-testid="section-desafio">
        <div className="label-mono">Diagnóstico</div>
        <Group label="¿Cuál es tu principal desafío?" options={CHALLENGES} keyName="desafio" testidPrefix="ret" />

        <div>
          <div className="label-mono mb-2 text-muted-foreground">Contexto adicional <span className="normal-case opacity-70">(opcional)</span></div>
          <textarea
            className="input-brut min-h-[80px]"
            placeholder="Ejemplo: ventas cayeron 20% desde julio, dependemos de 1 proveedor…"
            value={draft.answers.contexto || ""}
            onChange={(e) => setAnswer("contexto", e.target.value)}
            data-testid="input-contexto"
          />
        </div>
      </section>

      <button
        onClick={submit}
        disabled={!answered}
        data-testid="btn-negocio-continuar"
        className="btn-primary mt-8 w-full flex items-center justify-center gap-2 !py-4 !text-base"
      >
        Analizar mi negocio <ArrowRight size={16} weight="bold" />
      </button>
      {!answered && (
        <div className="text-xs text-muted-foreground mt-2 text-center">
          Nombre, industria, etapa, años y desafío son obligatorios. "Personas" y "Contexto" son opcionales.
        </div>
      )}
    </CompassLayout>
  );
}
